import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { AbortError, ServerError } from '@warp-drive/legacy/adapter/error';
import ApplicationAdapter from './application';

/** Retries after the first attempt, so a save is sent at most 4 times. */
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 1000;
const RETRY_FLOOR_MS = 250;

/**
 * Full-jitter exponential backoff: retry 0, 1, 2 waits a random time up to
 * 1 s, 2 s, 4 s respectively, and never less than 250 ms.
 *
 * Every repo status write — the front end's saves and xray-workflow's clone
 * callbacks alike — rewrites the same scan item, so under DynamoDB throttling
 * many writers fail together; the jitter keeps them from all coming back
 * together too.
 */
function retryDelayMs(retry: number): number {
  const ceilingMs = RETRY_BASE_MS * 2 ** retry;
  return Math.max(RETRY_FLOOR_MS, Math.random() * ceilingMs);
}

/**
 * A 5xx, a 429, or no response at all. Any other 4xx — e.g. the 422 for a scan
 * update past DynamoDB's expression size limit — fails the same way however
 * often it is sent.
 *
 * Decided by error class rather than `errors[0].status`: for a 422 whose body
 * isn't JSON:API (the API sends `{ error }`), Ember Data builds an
 * `InvalidError` with no status at all, which would read as "no response".
 */
function isTransient(error: any): boolean {
  if (error instanceof ServerError || error instanceof AbortError) return true;
  if (error?.isAdapterError) return error.errors?.[0]?.status === '429';
  // Not from the adapter at all: fetch itself rejected (network down, CORS).
  return true;
}

/**
 * Retries scan updates through transient failures. Only updates: the API
 * merges a PATCH's `repositories` by key, so resending one is safe, whereas a
 * retried create could make a second scan.
 */
export default class ScanAdapter extends ApplicationAdapter {
  @service declare intl: IntlService;
  @service declare notifications: any;

  /**
   * DynamoDB refuses a write once the scan item (or the update expression for
   * it) passes its size limit, and the API passes that on as a 422. Nothing
   * the user does on this page gets it under the limit, so they're told to
   * ask for help. Matched here rather than where the save fails: the API's
   * body isn't JSON:API, so the `InvalidError` Ember Data builds from it
   * keeps neither the status nor this message.
   */
  handleResponse(
    ...args: Parameters<ApplicationAdapter['handleResponse']>
  ): ReturnType<ApplicationAdapter['handleResponse']> {
    const [status, , payload] = args;
    if (
      status === 422 &&
      /exceeded the maximum allowed size/.test(
        String((payload as { error?: unknown } | null)?.error),
      )
    ) {
      this.notifications.toggleShowNotification(
        this.intl.t('scans.processing.scan-too-big'),
        'error',
      );
    }
    return super.handleResponse(...args);
  }

  async updateRecord(
    ...args: Parameters<ApplicationAdapter['updateRecord']>
  ): ReturnType<ApplicationAdapter['updateRecord']> {
    for (let retry = 0; retry < MAX_RETRIES; retry++) {
      try {
        return await super.updateRecord(...args);
      } catch (error) {
        if (!isTransient(error)) throw error;
      }
      await this.waitBeforeRetry(retryDelayMs(retry));
    }
    // Final attempt: whatever it throws is the caller's to handle.
    return super.updateRecord(...args);
  }

  /** Extracted so tests can override it on the instance rather than sleep. */
  waitBeforeRetry(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
